import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Button } from '@mui/material';
import { AnimatedDashOverlay, InnerDropZone, RootDropZone } from './blocks/DropZone';
import { emailBuilderBlockBackground } from './blocks/blockStyles';
import { EmailBlock, EmailBlockType } from './blocks/types';
import { Redo, Undo, Visibility, Extension, ZoomIn, ZoomOut, FitScreen, RestartAlt } from '@mui/icons-material';
import { HeadingBlock, HeadingBlockDialog } from './blocks/HeadingBlock';
import { ParagraphBlock, ParagraphBlockDialog } from './blocks/ParagraphBlock';
import { ImageBlock, ImageBlockDialog } from './blocks/ImageBlock';
import { ButtonBlock, ButtonBlockDialog, instantiateButtonBlock, renderButtonBlockToHtml } from './blocks/ButtonBlock';
import { DividerBlock } from './blocks/DividerBlock';
import { SpacerBlock } from './blocks/SpacerBlock';
import { AvatarBlock, AvatarBlockDialog, instantiateAvatarBlock, renderAvatarBlockToHtml } from './blocks/AvatarBlock';
import { ContainerBlock, ContainerBlockDialog, instantiateContainerBlock, renderContainerBlockToHtml } from './blocks/ContainerBlock';
import { ColumnBlock, ColumnBlockDialog, instantiateColumnBlock, renderColumnBlockToHtml } from './blocks/ColumnBlock';

// Using shared types from blocks/types

export type EmailBuilderValue = {
  blocks: EmailBlock[];
};

export type EmailBuilderProps = {
  value?: EmailBuilderValue;
  onChange?: (value: EmailBuilderValue) => void;
  onExportHtml?: (html: string) => void;
  /** When set, renders only the specified panel (hides palette). Used for mobile viewport. */
  mobileActivePanel?: 'layout' | 'preview';
};

const generateId = () => Math.random().toString(36).slice(2);

const defaultBlocks: EmailBlock[] = [
  { id: generateId(), type: 'heading', content: 'Welcome!' },
  { id: generateId(), type: 'paragraph', content: 'Start writing your email...' }
];

export const EmailBuilder: React.FC<EmailBuilderProps> = ({ value, onChange, onExportHtml, mobileActivePanel }) => {
  const [history, setHistory] = useState<EmailBuilderValue[]>([]);
  const [future, setFuture] = useState<EmailBuilderValue[]>([]);
  const [state, setState] = useState<EmailBuilderValue>({ blocks: value?.blocks?.length ? value.blocks : defaultBlocks });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [draftBlock, setDraftBlock] = useState<EmailBlock | null>(null);
  const [showPalette, setShowPalette] = useState(true);
  const [isPaletteCollapsed, setIsPaletteCollapsed] = useState(true);
  const [isCanvasCollapsed, setIsCanvasCollapsed] = useState(false);
  
  // Zoom state for Live Preview
  const [previewZoom, setPreviewZoom] = useState(100); // Zoom percentage
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  // Stable refs for callback props to avoid infinite-loop re-renders
  const onExportHtmlRef = useRef(onExportHtml);
  onExportHtmlRef.current = onExportHtml;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Initialize state from value prop when it changes
  useEffect(() => {
    if (value?.blocks?.length) {
      setState({ blocks: value.blocks });
    } else {
      setState({ blocks: defaultBlocks });
    }
  }, [value]);

  // Auto-export HTML whenever blocks change (use ref to avoid dep on callback identity)
  useEffect(() => {
    const html = renderBlocksToHtml(state.blocks);
    onExportHtmlRef.current?.(html);
  }, [state.blocks]);

  const commit = useCallback((next: EmailBuilderValue) => {
    setHistory(prev => [...prev, state]);
    setFuture([]);
    setState(next);
    onChangeRef.current?.(next);
  }, [state]);

  const undo = useCallback(() => {
    setHistory(prev => {
      if (prev.length === 0) return prev;
      const previous = prev[prev.length - 1];
      setFuture(f => [state, ...f]);
      setState(previous);
      onChangeRef.current?.(previous);
      return prev.slice(0, -1);
    });
  }, [state]);

  const redo = useCallback(() => {
    setFuture(prev => {
      if (prev.length === 0) return prev;
      const next = prev[0];
      setHistory(h => [...h, state]);
      setState(next);
      onChangeRef.current?.(next);
      return prev.slice(1);
    });
  }, [state]);

  // Instantiate a new block by type
  const instantiateBlock = useCallback((type: EmailBlockType): EmailBlock => {
    const id = generateId();
    if (type === 'heading') return { id, type, title: 'Heading', content: 'Your Heading', headingTag: 'h2', subHeading: '', align: 'left', fontColor: '#333333', subHeadingColor: '#555555' };
    if (type === 'paragraph') return { id, type, title: 'Paragraph', content: 'Your paragraph text' };
    if (type === 'button') return { ...instantiateButtonBlock(), id };
    if (type === 'image') return { id, type, title: 'Image', url: 'https://via.placeholder.com/600x200', align: 'center', width: 100 };
    if (type === 'divider') return { id, type, title: 'Divider' };
    if (type === 'spacer') return { id, type, title: 'Spacer' };
    if (type === 'avatar') return { ...instantiateAvatarBlock(), id };
    if (type === 'container') return { ...instantiateContainerBlock(), id };
    if (type === 'columns') return { ...instantiateColumnBlock(), id };
    return { id, type };
  }, []);

  const addBlock = useCallback((type: EmailBlockType) => {
    const block = instantiateBlock(type);
    commit({ blocks: [...state.blocks, block] });
  }, [state.blocks, commit, instantiateBlock]);

  // Deep helpers
  const updateDeep = useCallback((blocks: EmailBlock[], id: string, patch: Partial<EmailBlock>): EmailBlock[] => {
    return blocks.map(b => {
      if (b.id === id) return { ...b, ...patch };
      if (b.type === 'container' && b.children) return { ...b, children: updateDeep(b.children, id, patch) };
      if (b.type === 'columns' && b.columns) return { ...b, columns: b.columns.map(col => updateDeep(col, id, patch)) };
      return b;
    });
  }, []);

  const deleteDeep = useCallback((blocks: EmailBlock[], id: string): EmailBlock[] => {
    return blocks
      .filter(b => b.id !== id)
      .map(b => {
        if (b.type === 'container' && b.children) return { ...b, children: deleteDeep(b.children, id) };
        if (b.type === 'columns' && b.columns) return { ...b, columns: b.columns.map(col => deleteDeep(col, id)) };
        return b;
      });
  }, []);

  const findDeep = useCallback((blocks: EmailBlock[], id: string): EmailBlock | null => {
    for (const b of blocks) {
      if (b.id === id) return b;
      if (b.type === 'container' && b.children) {
        const found = findDeep(b.children, id);
        if (found) return found;
      }
      if (b.type === 'columns' && b.columns) {
        for (const col of b.columns) {
          const found = findDeep(col, id);
          if (found) return found;
        }
      }
    }
    return null;
  }, []);

  // Listen for double-click events from the Live Preview iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'EDIT_BLOCK' && event.data?.blockId) {
        const blockId = event.data.blockId;
        const block = findDeep(state.blocks, blockId);
        if (block) {
          setSelectedId(blockId);
          setDraftBlock(JSON.parse(JSON.stringify(block)) as EmailBlock);
          setEditOpen(true);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [state.blocks, findDeep]);

  const removeByIdDeep = useCallback((blocks: EmailBlock[], id: string): { next: EmailBlock[]; removed: EmailBlock | null } => {
    let removed: EmailBlock | null = null;
    const next = blocks
      .map(b => {
        if (b.id === id) {
          removed = b;
          return null;
        }
        if (b.type === 'container' && b.children) {
          const res = removeByIdDeep(b.children, id);
          if (res.removed) removed = res.removed;
          return { ...b, children: res.next };
        }
        if (b.type === 'columns' && b.columns) {
          const cols = b.columns.map(col => {
            const res = removeByIdDeep(col, id);
            if (res.removed) removed = res.removed;
            return res.next;
          });
          return { ...b, columns: cols };
        }
        return b;
      })
      .filter(Boolean) as EmailBlock[];
    return { next, removed };
  }, []);

  // Precise updaters for container and columns insertion
  const updateContainerChildrenById = useCallback((blocks: EmailBlock[], containerId: string, updater: (children: EmailBlock[]) => EmailBlock[]): EmailBlock[] => {
    return blocks.map(b => {
      if (b.id === containerId && b.type === 'container') {
        const current = b.children || [];
        return { ...b, children: updater(current) };
      }
      if (b.type === 'container' && b.children) return { ...b, children: updateContainerChildrenById(b.children, containerId, updater) };
      if (b.type === 'columns' && b.columns) return { ...b, columns: b.columns.map(col => updateContainerChildrenById(col, containerId, updater)) };
      return b;
    });
  }, []);

  const updateColumnsById = useCallback((blocks: EmailBlock[], columnsId: string, columnIndex: number, updater: (col: EmailBlock[]) => EmailBlock[]): EmailBlock[] => {
    return blocks.map(b => {
      if (b.id === columnsId && b.type === 'columns') {
        const cols = b.columns || [[], []];
        const idx = Math.max(0, Math.min(columnIndex, Math.max(0, cols.length - 1)));
        const nextCols = cols.map((c, i) => (i === idx ? updater(c) : c));
        return { ...b, columns: nextCols };
      }
      if (b.type === 'container' && b.children) return { ...b, children: updateColumnsById(b.children, columnsId, columnIndex, updater) };
      if (b.type === 'columns' && b.columns) return { ...b, columns: b.columns.map(col => updateColumnsById(col, columnsId, columnIndex, updater)) };
      return b;
    });
  }, []);

  const updateBlock = useCallback((id: string, patch: Partial<EmailBlock>) => {
    commit({ blocks: updateDeep(state.blocks, id, patch) });
  }, [state.blocks, commit, updateDeep]);

  const deleteBlock = useCallback((id: string) => {
    commit({ blocks: deleteDeep(state.blocks, id) });
    if (selectedId === id) setSelectedId(null);
  }, [state.blocks, commit, deleteDeep, selectedId]);

  const duplicateBlock = useCallback((id: string) => {
    const insertAfter = (blocks: EmailBlock[]): EmailBlock[] => {
      const result: EmailBlock[] = [];
      for (let i = 0; i < blocks.length; i++) {
        const b = blocks[i];
        result.push(b);
        if (b.id === id) {
          const clone = JSON.parse(JSON.stringify(b)) as EmailBlock;
          clone.id = generateId();
          result.push(clone);
        } else if (b.type === 'container' && b.children) {
          result[result.length - 1] = { ...b, children: insertAfter(b.children) };
        } else if (b.type === 'columns' && b.columns) {
          result[result.length - 1] = { ...b, columns: b.columns.map(col => insertAfter(col)) };
        }
      }
      return result;
    };
    commit({ blocks: insertAfter(state.blocks) });
  }, [state.blocks, commit]);

  // Drag & Drop
  type DragData = { kind: 'new'; type: EmailBlockType } | { kind: 'move'; id: string };

  const onPaletteDragStart = (type: EmailBlockType) => (e: React.DragEvent) => {
    const data: DragData = { kind: 'new', type };
    e.dataTransfer.setData('application/json', JSON.stringify(data));
    e.dataTransfer.effectAllowed = 'copyMove';
    setIsDragging(true);
  };

  const onBlockDragStart = (id: string) => (e: React.DragEvent) => {
    const data: DragData = { kind: 'move', id };
    e.dataTransfer.setData('application/json', JSON.stringify(data));
    e.dataTransfer.effectAllowed = 'move';
    // prevent parent blocks (e.g., columns) from also handling dragstart
    e.stopPropagation();
    setIsDragging(true);
  };

  const onAnyDragEnd = () => setIsDragging(false);

  const insertAtRootIndex = (index: number, data: DragData) => {
    if (data.kind === 'new') {
      const block = instantiateBlock(data.type);
      const next = [...state.blocks];
      next.splice(index, 0, block);
      commit({ blocks: next });
      return;
    }
    // move existing: remove then insert
    const { next, removed } = removeByIdDeep(state.blocks, data.id);
    if (!removed) return;
    const final = [...next];
    final.splice(Math.min(Math.max(index, 0), final.length), 0, removed);
    commit({ blocks: final });
  };

  const insertIntoContainer = (containerId: string, index: number, data: DragData) => {
    if (data.kind === 'new') {
      const newBlock = instantiateBlock(data.type);
      const blocks = updateContainerChildrenById(state.blocks, containerId, (children) => {
        const arr = [...children];
        const idx = Math.min(Math.max(index, 0), arr.length);
        arr.splice(idx, 0, newBlock);
        return arr;
      });
      commit({ blocks });
      return;
    }
    // move existing into container
    const { next, removed } = removeByIdDeep(state.blocks, data.id);
    if (!removed) return;
    const blocks = updateContainerChildrenById(next, containerId, (children) => {
      const arr = [...children];
      const idx = Math.min(Math.max(index, 0), arr.length);
      arr.splice(idx, 0, removed);
      return arr;
    });
    commit({ blocks });
  };

  const insertIntoColumns = (columnsId: string, colIndex: number, index: number, data: DragData) => {
    const targetColIdx = Math.max(0, colIndex);
    // Guard: do not allow dragging the entire columns block into its own columns
    if (data.kind === 'move' && data.id === columnsId) {
      return;
    }
    if (data.kind === 'new') {
      const child = instantiateBlock(data.type);
      const blocks = updateColumnsById(state.blocks, columnsId, targetColIdx, (col) => {
        const arr = [...col];
        const idx = Math.min(Math.max(index, 0), arr.length);
        arr.splice(idx, 0, child);
        return arr;
      });
      commit({ blocks });
      return;
    }
    // move existing into columns
    const { next, removed } = removeByIdDeep(state.blocks, data.id);
    if (!removed) return;
    const blocks = updateColumnsById(next, columnsId, targetColIdx, (col) => {
      const arr = [...col];
      const idx = Math.min(Math.max(index, 0), arr.length);
      arr.splice(idx, 0, removed);
      return arr;
    });
    commit({ blocks });
  };

  // Zoom handlers for Live Preview
  const handleZoomIn = useCallback(() => {
    setPreviewZoom(prev => Math.min(prev + 25, 200)); // Max 200%
  }, []);

  const handleZoomOut = useCallback(() => {
    setPreviewZoom(prev => Math.max(prev - 25, 25)); // Min 25%
  }, []);

  const handleZoomReset = useCallback(() => {
    setPreviewZoom(100); // Reset to 100%
  }, []);

  const handleZoomFit = useCallback(() => {
    if (previewContainerRef.current && previewIframeRef.current) {
      const containerHeight = previewContainerRef.current.clientHeight;
      const containerWidth = previewContainerRef.current.clientWidth;
      // Email content is typically 640px wide, estimate height based on content
      const contentWidth = 640;
      const contentHeight = previewIframeRef.current.contentDocument?.body?.scrollHeight || 800;
      
      // Calculate zoom to fit both width and height
      const widthRatio = (containerWidth / contentWidth) * 100;
      const heightRatio = (containerHeight / contentHeight) * 100;
      const fitZoom = Math.min(widthRatio, heightRatio, 100); // Don't exceed 100%
      
      setPreviewZoom(Math.max(Math.floor(fitZoom), 25)); // Min 25%
    }
  }, []);

  // (Duplicate auto-export removed — single effect above handles this via onExportHtmlRef)

  const canUndo = history.length > 0;
  const canRedo = future.length > 0;

  const selectedBlock = useMemo(() => selectedId ? findDeep(state.blocks, selectedId) : null, [selectedId, state.blocks, findDeep]);

  const getGridColumns = () => {
    const paletteWidth = !showPalette ? '' : (isPaletteCollapsed ? '40px' : '240px');
    const canvasWidth = isCanvasCollapsed ? '40px' : '1fr';
    const previewWidth = showPreview ? '1fr' : '';
    
    const columns = [paletteWidth, canvasWidth, previewWidth].filter(Boolean).join(' ');
    return columns || '1fr';
  };

  const isMobilePanel = !!mobileActivePanel;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: isMobilePanel ? '1fr' : { xs: '1fr', md: getGridColumns() }, gap: isMobilePanel ? 0 : 2, height: '100%', minHeight: 0, alignItems: 'stretch' }}>
      {/* Palette – hidden entirely when mobile panel mode is active */}
      {!isMobilePanel && showPalette && (
        isPaletteCollapsed ? (
          // Collapsed state - vertical bar with rotated text
          <Box
            onClick={() => setIsPaletteCollapsed(false)}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              backgroundColor: emailBuilderBlockBackground,
              transition: 'background-color 150ms ease, border-color 150ms ease',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: 'primary.main'
              }
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                transform: 'rotate(180deg)',
                whiteSpace: 'nowrap',
                fontWeight: 600,
                letterSpacing: 1
              }}
            >
              Building Blocks
            </Typography>
          </Box>
        ) : (
          // Expanded state - full panel
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
              <Typography variant="subtitle2">Blocks</Typography>
              <Tooltip title="Collapse">
                <IconButton 
                  size="small" 
                  onClick={() => setIsPaletteCollapsed(true)}
                  sx={{
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ 
                      width: 0, 
                      height: 0, 
                      borderTop: '5px solid transparent',
                      borderBottom: '5px solid transparent',
                      borderRight: '6px solid currentColor'
                    }} />
                  </Box>
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1, overflowY: 'auto' }}>
              {/* Use the same visual container style as blocks list items */}
              {([
                ['heading', 'Heading'],
                ['paragraph', 'Paragraph'],
                ['image', 'Image'],
                ['button', 'Button'],
                ['divider', 'Divider'],
                ['spacer', 'Spacer'],
                ['avatar', 'Avatar'],
                ['container', 'Container'],
                ['columns', 'Column (2-4)']
              ] as [EmailBlockType, string][]).map(([type, label]) => (
                <Box
                  key={type}
                  role="button"
                  tabIndex={0}
                  onClick={() => addBlock(type)}
                  draggable
                  onDragStart={onPaletteDragStart(type)}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    border: '1px solid',
                    borderRadius: 1,
                    borderColor: 'divider',
                    backgroundColor: emailBuilderBlockBackground,
                    cursor: 'grab',
                    transition: 'background-color 120ms ease, border-color 120ms ease',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderColor: 'primary.light'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                    <Extension sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ mb: 0, fontSize: '0.8rem' }}>{label}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )
      )}

      {/* Canvas (Layout Editor area) – hidden when mobile panel is 'preview' */}
      {(!isMobilePanel || mobileActivePanel === 'layout') && (isCanvasCollapsed ? (
        // Collapsed state - vertical bar with rotated text
        <Box
          onClick={() => setIsCanvasCollapsed(false)}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : '#fafafa',
            transition: 'background-color 150ms ease, border-color 150ms ease',
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'primary.main'
            }
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              transform: 'rotate(180deg)',
              whiteSpace: 'nowrap',
              fontWeight: 600,
              letterSpacing: 1
            }}
          >
            Layout Editor
          </Typography>
        </Box>
      ) : (
        // Expanded state - full canvas
        <Box
          sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: (t) => t.palette.mode === 'dark' ? 'grey.900' : '#fafafa', overflow: 'auto', minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
          onDrop={(e) => {
            // Only handle drops that target the canvas background (not inner dropzones)
            if (e.target !== e.currentTarget) return;
            const raw = e.dataTransfer.getData('application/json');
            if (!raw) return;
            const data = JSON.parse(raw) as DragData;
            insertAtRootIndex(state.blocks.length, data);
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexShrink: 0 }}>
            <Typography variant="subtitle2">Layout Editor</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Undo">
                <span>
                  <IconButton 
                    size="small" 
                    disabled={!canUndo} 
                    onClick={undo}
                    sx={{
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 0.5,
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      },
                      '&.Mui-disabled': { opacity: 0.4 }
                    }}
                  >
                    <Undo sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Redo">
                <span>
                  <IconButton 
                    size="small" 
                    disabled={!canRedo} 
                    onClick={redo}
                    sx={{
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 0.5,
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      },
                      '&.Mui-disabled': { opacity: 0.4 }
                    }}
                  >
                    <Redo sx={{ fontSize: 16 }} />
                  </IconButton>
                </span>
              </Tooltip>
              {!isMobilePanel && (
                <>
                  <Tooltip title="Preview">
                    <IconButton 
                      size="small" 
                      onClick={() => setPreviewOpen(true)}
                      sx={{
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 0.5,
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <Visibility sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ width: 1, height: 20, backgroundColor: 'divider', mx: 0.5 }} />
                  <Tooltip title="Collapse">
                    <IconButton 
                      size="small" 
                      onClick={() => setIsCanvasCollapsed(true)}
                      sx={{
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 0.5,
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ 
                          width: 0, 
                          height: 0, 
                          borderTop: '5px solid transparent',
                          borderBottom: '5px solid transparent',
                          borderRight: '6px solid currentColor'
                        }} />
                      </Box>
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, alignItems: 'start', flex: 1, minHeight: 0, overflow: 'auto' }}>
            {/* Editor Canvas */}
            <Box sx={{ mx: 'auto', width: '100%', backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <RootDropZone isDragging={isDragging} onDrop={(d) => { insertAtRootIndex(0, d); setIsDragging(false); }} />
          {state.blocks.map((block, idx) => (
            <React.Fragment key={block.id}>
              <BlockEditor
                block={block}
                selectedId={selectedId}
                  onSelect={(id) => {
                    setSelectedId(id);
                    const b = findDeep(state.blocks, id);
                    if (b) {
                      setDraftBlock(JSON.parse(JSON.stringify(b)) as EmailBlock);
                      setEditOpen(true);
                    }
                  }}
                onChange={updateBlock}
                onDelete={deleteBlock}
                onDuplicate={duplicateBlock}
                onDragStart={onBlockDragStart}
                onDragEnd={onAnyDragEnd}
                isDragging={isDragging}
                insertIntoContainer={insertIntoContainer}
                insertIntoColumns={insertIntoColumns}
              />
              <RootDropZone isDragging={isDragging} onDrop={(d) => { insertAtRootIndex(idx + 1, d); setIsDragging(false); }} />
            </React.Fragment>
          ))}
            </Box>

            {/* Properties Panel moved inside Layout Editor area */}

          </Box>
        </Box>
      ))}

      {/* Live Preview Pane (right column) – hidden when mobile panel is 'layout' */}
      {(!isMobilePanel ? showPreview : mobileActivePanel === 'preview') && (
        <Box sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1, 
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 35, 60, 0.6)' : 'rgba(240, 242, 245, 0.8)',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            flexShrink: 0,
            px: 2,
            py: 1,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(40, 50, 80, 0.9)' : 'rgba(225, 230, 240, 0.95)',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Live Preview</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobilePanel ? 1 : 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5, fontWeight: 500 }}>{previewZoom}%</Typography>
              <Tooltip title="Zoom Out">
                <span>
                <IconButton 
                  size="small" 
                  onClick={handleZoomOut}
                  disabled={previewZoom <= 25}
                  sx={{ 
                    borderRadius: 1, 
                    border: '1px solid', 
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(100, 120, 180, 0.5)' : 'rgba(100, 120, 180, 0.4)',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(60, 80, 130, 0.4)' : 'rgba(255, 255, 255, 0.9)',
                    p: isMobilePanel ? 1 : 0.5,
                    minWidth: isMobilePanel ? 40 : 'auto',
                    minHeight: isMobilePanel ? 40 : 'auto',
                    '&:hover': { 
                      borderColor: 'primary.main', 
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(80, 100, 160, 0.6)' : 'rgba(200, 220, 255, 0.9)'
                    },
                    '&.Mui-disabled': {
                      opacity: 0.4
                    }
                  }}
                >
                  <ZoomOut sx={{ fontSize: isMobilePanel ? 22 : 16 }} />
                </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Zoom In">
                <span>
                <IconButton 
                  size="small" 
                  onClick={handleZoomIn}
                  disabled={previewZoom >= 200}
                  sx={{ 
                    borderRadius: 1, 
                    border: '1px solid', 
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(100, 120, 180, 0.5)' : 'rgba(100, 120, 180, 0.4)',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(60, 80, 130, 0.4)' : 'rgba(255, 255, 255, 0.9)',
                    p: isMobilePanel ? 1 : 0.5,
                    minWidth: isMobilePanel ? 40 : 'auto',
                    minHeight: isMobilePanel ? 40 : 'auto',
                    '&:hover': { 
                      borderColor: 'primary.main', 
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(80, 100, 160, 0.6)' : 'rgba(200, 220, 255, 0.9)'
                    },
                    '&.Mui-disabled': {
                      opacity: 0.4
                    }
                  }}
                >
                  <ZoomIn sx={{ fontSize: isMobilePanel ? 22 : 16 }} />
                </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Fit to Frame">
                <IconButton 
                  size="small" 
                  onClick={handleZoomFit}
                  sx={{ 
                    borderRadius: 1, 
                    border: '1px solid', 
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(100, 120, 180, 0.5)' : 'rgba(100, 120, 180, 0.4)',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(60, 80, 130, 0.4)' : 'rgba(255, 255, 255, 0.9)',
                    p: isMobilePanel ? 1 : 0.5,
                    minWidth: isMobilePanel ? 40 : 'auto',
                    minHeight: isMobilePanel ? 40 : 'auto',
                    '&:hover': { 
                      borderColor: 'primary.main', 
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(80, 100, 160, 0.6)' : 'rgba(200, 220, 255, 0.9)'
                    }
                  }}
                >
                  <FitScreen sx={{ fontSize: isMobilePanel ? 22 : 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset Zoom (100%)">
                <span>
                <IconButton 
                  size="small" 
                  onClick={handleZoomReset}
                  disabled={previewZoom === 100}
                  sx={{ 
                    borderRadius: 1, 
                    border: '1px solid', 
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(100, 120, 180, 0.5)' : 'rgba(100, 120, 180, 0.4)',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(60, 80, 130, 0.4)' : 'rgba(255, 255, 255, 0.9)',
                    p: isMobilePanel ? 1 : 0.5,
                    minWidth: isMobilePanel ? 40 : 'auto',
                    minHeight: isMobilePanel ? 40 : 'auto',
                    '&:hover': { 
                      borderColor: 'primary.main', 
                      backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(80, 100, 160, 0.6)' : 'rgba(200, 220, 255, 0.9)'
                    },
                    '&.Mui-disabled': {
                      opacity: 0.4
                    }
                  }}
                >
                  <RestartAlt sx={{ fontSize: isMobilePanel ? 22 : 16 }} />
                </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <Box 
            ref={previewContainerRef}
            sx={{ 
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              position: 'relative',
              m: 1.5,
              borderRadius: 1,
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(20, 25, 45, 0.5)' : 'rgba(255, 255, 255, 0.6)',
              boxShadow: (theme) => theme.palette.mode === 'dark' ? 'inset 0 1px 3px rgba(0,0,0,0.3)' : 'inset 0 1px 3px rgba(0,0,0,0.08)'
            }}
          >
            <Box sx={{
              width: `${10000 / previewZoom}%`,
              height: `${10000 / previewZoom}%`,
              transform: `scale(${previewZoom / 100})`,
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: 0
            }}>
              <iframe 
                ref={previewIframeRef}
                title="email-preview-live" 
                style={{ width: '100%', height: '100%', minHeight: 300, border: 'none' }} 
                srcDoc={renderBlocksToHtmlForPreview(state.blocks)} 
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email preview</DialogTitle>
        <DialogContent>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <iframe title="email-preview" style={{ width: '100%', height: 600, border: 'none' }} srcDoc={renderBlocksToHtml(state.blocks)} />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Block Properties Dialogs */}
      {/* Heading specific dialog */}
      {editOpen && draftBlock && draftBlock.type === 'heading' && (
        <HeadingBlockDialog
          open={editOpen}
          draft={draftBlock}
          setDraft={(d) => setDraftBlock({ ...d })}
          onCancel={() => setEditOpen(false)}
          onConfirm={() => {
            if (selectedId && draftBlock) {
              const patch: Partial<EmailBlock> = { ...draftBlock };
              delete (patch as any).id;
              updateBlock(selectedId, patch);
            }
            setEditOpen(false);
          }}
        />
      )}
      {/* Paragraph specific dialog */}
      {editOpen && draftBlock && draftBlock.type === 'paragraph' && (
        <ParagraphBlockDialog
          open={editOpen}
          draft={draftBlock}
          setDraft={(d) => setDraftBlock({ ...d })}
          onCancel={() => setEditOpen(false)}
          onConfirm={() => {
            if (selectedId && draftBlock) {
              const patch: Partial<EmailBlock> = { ...draftBlock };
              delete (patch as any).id;
              updateBlock(selectedId, patch);
            }
            setEditOpen(false);
          }}
        />
      )}
      {/* Image specific dialog */}
      {editOpen && draftBlock && draftBlock.type === 'image' && (
        <ImageBlockDialog
          open={editOpen}
          draft={draftBlock}
          setDraft={(d) => setDraftBlock({ ...d })}
          onCancel={() => setEditOpen(false)}
          onConfirm={() => {
            if (selectedId && draftBlock) {
              const patch: Partial<EmailBlock> = { ...draftBlock };
              delete (patch as any).id;
              updateBlock(selectedId, patch);
            }
            setEditOpen(false);
          }}
        />
      )}
      {/* Button specific dialog */}
      {editOpen && draftBlock && draftBlock.type === 'button' && (
        <ButtonBlockDialog
          open={editOpen}
          draft={draftBlock}
          setDraft={(d) => setDraftBlock({ ...d })}
          onCancel={() => setEditOpen(false)}
          onConfirm={() => {
            if (selectedId && draftBlock) {
              const patch: Partial<EmailBlock> = { ...draftBlock };
              delete (patch as any).id;
              updateBlock(selectedId, patch);
            }
            setEditOpen(false);
          }}
        />
      )}
      {/* Avatar specific dialog */}
      {editOpen && draftBlock && draftBlock.type === 'avatar' && (
        <AvatarBlockDialog
          open={editOpen}
          draft={draftBlock}
          setDraft={(d) => setDraftBlock({ ...d })}
          onCancel={() => setEditOpen(false)}
          onConfirm={() => {
            if (selectedId && draftBlock) {
              const patch: Partial<EmailBlock> = { ...draftBlock };
              delete (patch as any).id;
              updateBlock(selectedId, patch);
            }
            setEditOpen(false);
          }}
        />
      )}
      {/* Container specific dialog */}
        {editOpen && draftBlock && draftBlock.type === 'container' && (
          <ContainerBlockDialog
            open={editOpen}
            draft={draftBlock}
            setDraft={(d) => setDraftBlock({ ...d })}
            onCancel={() => setEditOpen(false)}
            onConfirm={() => {
              if (selectedId && draftBlock) {
                const patch: Partial<EmailBlock> = { ...draftBlock };
                delete (patch as any).id;
                updateBlock(selectedId, patch);
              }
              setEditOpen(false);
            }}
          />
        )}
        {editOpen && draftBlock && draftBlock.type === 'columns' && (
          <ColumnBlockDialog
            open={editOpen}
            draft={draftBlock}
            setDraft={(d) => setDraftBlock({ ...d })}
            onCancel={() => setEditOpen(false)}
            onConfirm={() => {
              if (selectedId && draftBlock) {
                const patch: Partial<EmailBlock> = { ...draftBlock };
                delete (patch as any).id;
                updateBlock(selectedId, patch);
              }
              setEditOpen(false);
            }}
          />
        )}
      {/* Generic dialog for other block types */}
      {editOpen && draftBlock && draftBlock.type !== 'heading' && draftBlock.type !== 'paragraph' && draftBlock.type !== 'image' && draftBlock.type !== 'button' && draftBlock.type !== 'avatar' && draftBlock.type !== 'container' && draftBlock.type !== 'columns' && (
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Block</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>Title</Typography>
              <input value={draftBlock.title || ''} onChange={(e) => setDraftBlock({ ...draftBlock, title: e.target.value })} style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, marginBottom: 12 }} />
              <DialogActions>
                <Button variant="outlined" color="secondary" onClick={() => setEditOpen(false)}>Cancel</Button>
                <Button color="success" variant="contained" onClick={() => {
                  if (selectedId && draftBlock) {
                    const patch: Partial<EmailBlock> = { ...draftBlock };
                    delete (patch as any).id;
                    updateBlock(selectedId, patch);
                  }
                  setEditOpen(false);
                }}>OK</Button>
              </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
      )}
    </Box>
  );
};

// Drop zones moved to blocks/DropZone

// Recursive block editor
const BlockEditor: React.FC<{
  block: EmailBlock;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onChange: (id: string, patch: Partial<EmailBlock>) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDragStart: (id: string) => (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  insertIntoContainer: (containerId: string, index: number, data: any) => void;
  insertIntoColumns: (columnsId: string, columnIndex: number, index: number, data: any) => void;
  isDragging?: boolean;
}> = ({ block, selectedId, onSelect, onChange, onDelete, onDuplicate, onDragStart, onDragEnd, insertIntoContainer, insertIntoColumns, isDragging }) => {
  const isSelected = selectedId === block.id;
  const common = { px: 2, py: 1.5, border: '1px solid', borderColor: isSelected ? 'primary.main' : 'transparent', cursor: 'move' } as const;

  if (block.type === 'heading') {
    return (
      <HeadingBlock
        block={block}
        isSelected={isSelected}
        onSelect={() => onSelect(block.id)}
        onChange={(patch) => onChange(block.id, patch)}
        onDelete={() => onDelete(block.id)}
        onDuplicate={() => onDuplicate(block.id)}
        onDragStart={onDragStart(block.id)}
        onDragEnd={onDragEnd}
      />
    );
  }

  if (block.type === 'paragraph') {
    return (
      <ParagraphBlock
        block={block}
        isSelected={isSelected}
        onSelect={() => onSelect(block.id)}
        onChange={(patch) => onChange(block.id, patch)}
        onDelete={() => onDelete(block.id)}
        onDuplicate={() => onDuplicate(block.id)}
        onDragStart={onDragStart(block.id)}
        onDragEnd={onDragEnd}
      />
    );
  }

  if (block.type === 'image') {
    return (
      <ImageBlock
        block={block}
        isSelected={isSelected}
        onSelect={() => onSelect(block.id)}
        onChange={(patch) => onChange(block.id, patch)}
        onDelete={() => onDelete(block.id)}
        onDuplicate={() => onDuplicate(block.id)}
        onDragStart={onDragStart(block.id)}
        onDragEnd={onDragEnd}
      />
    );
  }

  if (block.type === 'button') {
    return (
      <ButtonBlock
        block={block}
        isSelected={isSelected}
        onSelect={() => onSelect(block.id)}
        onChange={(patch) => onChange(block.id, patch)}
        onDelete={() => onDelete(block.id)}
        onDuplicate={() => onDuplicate(block.id)}
        onDragStart={onDragStart(block.id)}
        onDragEnd={onDragEnd}
      />
    );
  }

  if (block.type === 'divider') {
    return (
      <DividerBlock
        block={block}
        isSelected={isSelected}
        onSelect={() => onSelect(block.id)}
        onDelete={() => onDelete(block.id)}
        onDuplicate={() => onDuplicate(block.id)}
        onDragStart={onDragStart(block.id)}
        onDragEnd={onDragEnd}
      />
    );
  }

  if (block.type === 'spacer') {
    return (
      <SpacerBlock
        block={block}
        isSelected={isSelected}
        onSelect={() => onSelect(block.id)}
        onDelete={() => onDelete(block.id)}
        onDuplicate={() => onDuplicate(block.id)}
        onDragStart={onDragStart(block.id)}
        onDragEnd={onDragEnd}
      />
    );
  }

  if (block.type === 'avatar') {
    return (
      <AvatarBlock
        block={block}
        isSelected={isSelected}
        onSelect={() => onSelect(block.id)}
        onChange={(patch) => onChange(block.id, patch)}
        onDelete={() => onDelete(block.id)}
        onDuplicate={() => onDuplicate(block.id)}
        onDragStart={onDragStart(block.id)}
        onDragEnd={onDragEnd}
      />
    );
  }

  if (block.type === 'container') {
    const children = block.children || [];
    return (
      <ContainerBlock
        block={block}
        isSelected={isSelected}
        isDragging={!!isDragging}
        onSelect={() => onSelect(block.id)}
        onChange={(patch) => onChange(block.id, patch)}
        onDelete={() => onDelete(block.id)}
        onDuplicate={() => onDuplicate(block.id)}
        onDragStart={onDragStart(block.id)}
        onDragEnd={onDragEnd}
        renderChild={(child) => (
          <BlockEditor
            block={child}
            selectedId={selectedId}
            onSelect={onSelect}
            onChange={onChange}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDragging={isDragging}
            insertIntoContainer={insertIntoContainer}
            insertIntoColumns={insertIntoColumns}
          />
        )}
        insertAt={(index, data) => insertIntoContainer(block.id, index, data)}
      />
    );
  }

  if (block.type === 'columns') {
    const cols = block.columns || [[], []];
    return (
      <ColumnBlock
        block={block}
        isSelected={isSelected}
        isDragging={!!isDragging}
        onSelect={() => onSelect(block.id)}
        onChange={(patch) => onChange(block.id, patch)}
        onDelete={() => onDelete(block.id)}
        onDuplicate={() => onDuplicate(block.id)}
        onDragStart={onDragStart(block.id)}
        onDragEnd={onDragEnd}
        renderChild={(child: EmailBlock) => (
          <BlockEditor
            block={child}
            selectedId={selectedId}
            onSelect={onSelect}
            onChange={onChange}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            isDragging={isDragging}
            insertIntoContainer={insertIntoContainer}
            insertIntoColumns={insertIntoColumns}
          />
        )}
        insertAt={(index: number, data: any, columnIdx?: number) => {
          if (columnIdx !== undefined) {
            insertIntoColumns(block.id, columnIdx, index, data);
          }
        }}
      />
    );
  }

  return null;
};

// Legacy RowActions removed; new RowActions live under blocks/RowActions.tsx

function escapeHtml(text: string) {
  return (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function renderBlockToHtml(block: EmailBlock): string {
  switch (block.type) {
    case 'heading':
      const tag = (block.headingTag || 'h2').toLowerCase();
      const textAlign = block.align || 'left';
      const fontColor = block.fontColor || '#333333';
      const subHeadingColor = block.subHeadingColor || '#555555';
      const topMargin = block.topMargin || 0;
      const main = `<${tag} style="margin:0; padding:0; text-align:${textAlign}; font-family:Arial, Helvetica, sans-serif; color:${fontColor};">${escapeHtml(block.content || '')}</${tag}>`;
      const sub = block.subHeading ? `<span style="display:block; margin-top:6px; color:${subHeadingColor}; text-align:${textAlign}; font-family:Arial, Helvetica, sans-serif;">${escapeHtml(block.subHeading)}</span>` : '';
      return `<tr><td style="padding-top:${16 + topMargin}px; padding-right:24px; padding-bottom:16px; padding-left:24px;">${main}${sub}</td></tr>`;
    case 'paragraph': {
      // Process rich text content from Quill
      let content = block.content || '';
      
      // Ensure proper email-safe styling for rich text content
      if (content.includes('<')) {
        // Step 1: Convert Quill alignment classes to inline styles before removing classes
        // This function extracts alignment from class and applies it as inline style
        const convertAlignmentClass = (_match: string, tagName: string, beforeAttrs: string, classValue: string) => {
          let textAlign = '';
          // Extract alignment from Quill classes
          if (classValue.includes('ql-align-center')) {
            textAlign = 'text-align:center;';
          } else if (classValue.includes('ql-align-right')) {
            textAlign = 'text-align:right;';
          } else if (classValue.includes('ql-align-justify')) {
            textAlign = 'text-align:justify;';
          } else {
            textAlign = 'text-align:left;';
          }
          
          // Check if there's already a style attribute
          if (beforeAttrs.includes('style="')) {
            return `<${tagName}${beforeAttrs.replace(/style="/, `style="${textAlign} `)}`;
          } else {
            return `<${tagName}${beforeAttrs} style="${textAlign}"`;
          }
        };
        
        content = content
          // Convert alignment for paragraphs
          .replace(/<p([^>]*?)\sclass="([^"]*)"/g, (match, beforeAttrs, classValue) => 
            convertAlignmentClass(match, 'p', beforeAttrs, classValue))
          // Convert alignment for all heading levels (h1-h6)
          .replace(/<h1([^>]*?)\sclass="([^"]*)"/g, (match, beforeAttrs, classValue) => 
            convertAlignmentClass(match, 'h1', beforeAttrs, classValue))
          .replace(/<h2([^>]*?)\sclass="([^"]*)"/g, (match, beforeAttrs, classValue) => 
            convertAlignmentClass(match, 'h2', beforeAttrs, classValue))
          .replace(/<h3([^>]*?)\sclass="([^"]*)"/g, (match, beforeAttrs, classValue) => 
            convertAlignmentClass(match, 'h3', beforeAttrs, classValue))
          .replace(/<h4([^>]*?)\sclass="([^"]*)"/g, (match, beforeAttrs, classValue) => 
            convertAlignmentClass(match, 'h4', beforeAttrs, classValue))
          .replace(/<h5([^>]*?)\sclass="([^"]*)"/g, (match, beforeAttrs, classValue) => 
            convertAlignmentClass(match, 'h5', beforeAttrs, classValue))
          .replace(/<h6([^>]*?)\sclass="([^"]*)"/g, (match, beforeAttrs, classValue) => 
            convertAlignmentClass(match, 'h6', beforeAttrs, classValue));
        
        // Step 2: Remove ALL class attributes (after converting alignment to inline styles)
        content = content
          .replace(/\sclass="[^"]*"/g, '')
          // Remove Outlook-specific x_ attributes
          .replace(/\sx_[a-zA-Z0-9_-]+="[^"]*"/g, '')
          // Remove data- attributes
          .replace(/\sdata-[a-zA-Z0-9_-]+="[^"]*"/g, '');
        
        // Step 3: Ensure lists have proper email-safe styles
        content = content
          .replace(/<ul>/g, '<ul style="margin:8px 0; padding-left:20px; list-style-type:disc;">')
          .replace(/<ol>/g, '<ol style="margin:8px 0; padding-left:20px;">')
          .replace(/<li>/g, '<li style="margin:4px 0;">');
        
        // Step 4: Ensure all paragraphs and headings have basic styles
        content = content
          .replace(/<p\s+style="([^"]*)"/g, (_match, existingStyle) => {
            let style = existingStyle;
            // Add margin and padding if not present
            if (!style.includes('margin')) style = 'margin:0; ' + style;
            if (!style.includes('padding')) style = 'padding:0; ' + style;
            // Ensure text-align is present (default to left if not specified)
            if (!style.includes('text-align')) style = style + ' text-align:left;';
            return `<p style="${style}"`;
          })
          .replace(/<p>/g, '<p style="margin:0; padding:0; text-align:left;">')
          // Ensure headings have basic styles with text-align
          .replace(/<(h[1-6])\s+style="([^"]*)"/g, (_match, tag, existingStyle) => {
            let style = existingStyle;
            // Add margin and padding if not present
            if (!style.includes('margin')) style = 'margin:0; ' + style;
            if (!style.includes('padding')) style = 'padding:0; ' + style;
            // Ensure text-align is present (default to left if not specified)
            if (!style.includes('text-align')) style = style + ' text-align:left;';
            return `<${tag} style="${style}"`;
          })
          .replace(/<(h[1-6])>/g, '<$1 style="margin:0; padding:0; text-align:left;">');
        
        // Step 5: Ensure links open in new tab
        content = content
          .replace(/<a\s+href="([^"]*)"/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:#0066cc; text-decoration:underline;"');
      }
      
      return `<tr><td style="padding:12px 24px; font-size:14px; line-height:20px; font-family:Arial, Helvetica, sans-serif; color:#333">${content}</td></tr>`;
    }
    case 'image': {
      const align = block.align || 'center';
      const width = block.width || 100;
      const imageUrl = block.imageUrl || block.url || '';
      return `<tr><td style="padding:8px 24px; text-align:${align}"><img src="${escapeHtml(imageUrl)}" style="max-width:100%; width:${width}%; height:auto;" /></td></tr>`;
    }
    case 'button':
      return renderButtonBlockToHtml(block);
    case 'divider':
      return `<tr><td style="padding:12px 24px"><hr style="border:none; border-top:1px solid #e5e7eb" /></td></tr>`;
    case 'spacer':
      return `<tr><td style="padding:16px 24px"></td></tr>`;
    case 'avatar':
      return renderAvatarBlockToHtml(block);
    case 'container':
      return renderContainerBlockToHtml(block, renderBlockToHtml);
    case 'columns':
      return renderColumnBlockToHtml(block, renderBlockToHtml);
    default:
      return '';
  }
}

function renderBlocksToHtml(blocks: EmailBlock[]): string {
  const rows = blocks.map(renderBlockToHtml).join('');
  return `<!doctype html>
<html>
  <head>
    <meta charSet="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Email</title>
  </head>
  <body style="margin:0; padding:0; background:#f6f7f9;">
    <table role="presentation" width="100%" cellPadding="0" cellSpacing="0" style="background:#f6f7f9;">
      <tbody>
        <tr>
          <td>
            <table role="presentation" width="640" align="center" cellPadding="0" cellSpacing="0" style="margin:24px auto; background:#ffffff; border:1px solid #e5e7eb; border-radius:8px;">
              <tbody>
                ${rows}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  </body>
 </html>`;
}

// Render a single block with data-block-id for preview (enables double-click to edit)
function renderBlockToHtmlForPreview(block: EmailBlock): string {
  const baseHtml = renderBlockToHtml(block);
  // Add data-block-id attribute to the <tr> element
  return baseHtml.replace('<tr>', `<tr data-block-id="${block.id}" style="cursor:pointer;">`);
}

// Preview-only version with hover styles for building blocks
function renderBlocksToHtmlForPreview(blocks: EmailBlock[]): string {
  const rows = blocks.map(renderBlockToHtmlForPreview).join('');
  return `<!doctype html>
<html>
  <head>
    <meta charSet="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Email Preview</title>
    <style>
      /* Preview-only hover styles - these will NOT be included in actual emails */
      table[role="presentation"] table[role="presentation"] > tbody > tr[data-block-id] {
        transition: outline 0.15s ease, background-color 0.15s ease;
        outline: 2px solid transparent;
        outline-offset: -2px;
        cursor: pointer;
      }
      table[role="presentation"] table[role="presentation"] > tbody > tr[data-block-id]:hover {
        outline: 2px dashed #1976d2;
        background-color: rgba(25, 118, 210, 0.04);
      }
      table[role="presentation"] table[role="presentation"] > tbody > tr[data-block-id]:hover > td {
        position: relative;
      }
      table[role="presentation"] table[role="presentation"] > tbody > tr[data-block-id]:hover > td::after {
        content: 'Double-click to edit';
        position: absolute;
        top: 4px;
        right: 4px;
        font-size: 10px;
        color: #1976d2;
        background: rgba(255,255,255,0.9);
        padding: 2px 6px;
        border-radius: 3px;
        font-family: Arial, sans-serif;
        pointer-events: none;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      }
    </style>
  </head>
  <body style="margin:0; padding:0; background:#f6f7f9;">
    <table role="presentation" width="100%" cellPadding="0" cellSpacing="0" style="background:#f6f7f9;">
      <tbody>
        <tr>
          <td>
            <table role="presentation" width="640" align="center" cellPadding="0" cellSpacing="0" style="margin:24px auto; background:#ffffff; border:1px solid #e5e7eb; border-radius:8px;">
              <tbody>
                ${rows}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
    <script>
      function findBlockRow(el) {
        while (el && el.tagName !== 'TR') el = el.parentElement;
        return (el && el.hasAttribute('data-block-id')) ? el : null;
      }
      function editBlock(row) {
        if (row) window.parent.postMessage({ type: 'EDIT_BLOCK', blockId: row.getAttribute('data-block-id') }, '*');
      }

      // Desktop: double-click
      document.addEventListener('dblclick', function(e) { editBlock(findBlockRow(e.target)); });

      // Mobile: double-tap (two taps within 350ms)
      var lastTapTime = 0;
      var lastTapTarget = null;
      document.addEventListener('touchend', function(e) {
        var now = Date.now();
        var row = findBlockRow(e.target);
        if (row && row === lastTapTarget && now - lastTapTime < 350) {
          e.preventDefault();
          editBlock(row);
          lastTapTime = 0;
          lastTapTarget = null;
        } else {
          lastTapTime = now;
          lastTapTarget = row;
        }
      });
    </script>
  </body>
 </html>`;
}

export default EmailBuilder;


